/**
 * sky.ts — Mediterranean sky dome, sun, and distance haze.
 *
 * Gradient dome shader (zenith/horizon + sun disc glow + warm haze band) with
 * time-of-day keyframes. Also drives scene fog and light rig so the whole
 * environment stays coherent from dawn to night.
 */
import * as THREE from "three";
import { clamp01 } from "../../sim/pcg";

export type TimeOfDay = number; // 0..1, 0 = midnight, 0.5 = noon

interface SkyKey {
  t: number;
  zenith: THREE.Color;
  horizon: THREE.Color;
  haze: THREE.Color;
  sun: THREE.Color;
  sunI: number;
  hemiSky: THREE.Color;
  hemiGround: THREE.Color;
  hemiI: number;
  fog: THREE.Color;
  fogNear: number;
  fogFar: number;
  exposure: number;
}

const C = (hex: number) => new THREE.Color(hex);

const KEYS: SkyKey[] = [
  { t: 0.0, zenith: C(0x060a14), horizon: C(0x0d1420), haze: C(0x101820), sun: C(0x223044), sunI: 0.0, hemiSky: C(0x1a2436), hemiGround: C(0x0c0a08), hemiI: 0.25, fog: C(0x0b1018), fogNear: 400, fogFar: 4500, exposure: 0.7 },
  { t: 0.22, zenith: C(0x2a3d5c), horizon: C(0xc98a5a), haze: C(0xd9a06a), sun: C(0xffb36b), sunI: 1.4, hemiSky: C(0x5a6c8c), hemiGround: C(0x3a2f22), hemiI: 0.5, fog: C(0xb98a64), fogNear: 700, fogFar: 6500, exposure: 0.95 },
  { t: 0.3, zenith: C(0x3d6cae), horizon: C(0xcfd8ce), haze: C(0xe0d9c2), sun: C(0xffe9c4), sunI: 2.4, hemiSky: C(0xbdd3e6), hemiGround: C(0x5a4a34), hemiI: 0.75, fog: C(0xcfd4c8), fogNear: 1000, fogFar: 8000, exposure: 1.0 },
  { t: 0.5, zenith: C(0x2f66b0), horizon: C(0xbcd0d8), haze: C(0xd8dcc8), sun: C(0xfff3dd), sunI: 2.8, hemiSky: C(0xbdd3e6), hemiGround: C(0x6a5a40), hemiI: 0.85, fog: C(0xc3cfd2), fogNear: 1200, fogFar: 9500, exposure: 1.05 },
  { t: 0.68, zenith: C(0x3a5f9e), horizon: C(0xe0b070), haze: C(0xe8c088), sun: C(0xffc37a), sunI: 2.2, hemiSky: C(0xa8b4c4), hemiGround: C(0x54432c), hemiI: 0.7, fog: C(0xd0b184), fogNear: 900, fogFar: 7500, exposure: 1.0 },
  { t: 0.78, zenith: C(0x1e2c50), horizon: C(0xd96f4a), haze: C(0xe08a58), sun: C(0xff8a4d), sunI: 1.2, hemiSky: C(0x4a5468), hemiGround: C(0x2e2620), hemiI: 0.45, fog: C(0xa8785c), fogNear: 600, fogFar: 6000, exposure: 0.9 },
  { t: 0.86, zenith: C(0x0a1024), horizon: C(0x2a2438), haze: C(0x3a3040), sun: C(0x5a4a58), sunI: 0.15, hemiSky: C(0x232c40), hemiGround: C(0x100e0c), hemiI: 0.3, fog: C(0x1a1c28), fogNear: 450, fogFar: 4800, exposure: 0.75 },
  { t: 1.0, zenith: C(0x060a14), horizon: C(0x0d1420), haze: C(0x101820), sun: C(0x223044), sunI: 0.0, hemiSky: C(0x1a2436), hemiGround: C(0x0c0a08), hemiI: 0.25, fog: C(0x0b1018), fogNear: 400, fogFar: 4500, exposure: 0.7 },
];

function lerpKey(a: SkyKey, b: SkyKey, t: number): SkyKey {
  const mix = (ca: THREE.Color, cb: THREE.Color) => ca.clone().lerp(cb, t);
  return {
    t,
    zenith: mix(a.zenith, b.zenith), horizon: mix(a.horizon, b.horizon),
    haze: mix(a.haze, b.haze), sun: mix(a.sun, b.sun),
    sunI: a.sunI + (b.sunI - a.sunI) * t,
    hemiSky: mix(a.hemiSky, b.hemiSky), hemiGround: mix(a.hemiGround, b.hemiGround),
    hemiI: a.hemiI + (b.hemiI - a.hemiI) * t,
    fog: mix(a.fog, b.fog),
    fogNear: a.fogNear + (b.fogNear - a.fogNear) * t,
    fogFar: a.fogFar + (b.fogFar - a.fogFar) * t,
    exposure: a.exposure + (b.exposure - a.exposure) * t,
  };
}

export function sampleSky(t: number): SkyKey {
  const tt = ((t % 1) + 1) % 1;
  for (let i = 0; i < KEYS.length - 1; i++) {
    if (tt >= KEYS[i].t && tt <= KEYS[i + 1].t) {
      const f = (tt - KEYS[i].t) / (KEYS[i + 1].t - KEYS[i].t || 1);
      return lerpKey(KEYS[i], KEYS[i + 1], clamp01(f));
    }
  }
  return KEYS[KEYS.length - 1];
}

export interface SkyRig {
  group: THREE.Group;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  setTimeOfDay(t: TimeOfDay, scene: THREE.Scene, renderer?: THREE.WebGLRenderer): void;
  dispose(): void;
}

/** Sun elevation/azimuth path for time t (0=midnight .. 0.5=noon). */
export function sunDirection(t: TimeOfDay): THREE.Vector3 {
  // Sunrise ~0.22, sunset ~0.78, matching the sky keyframes.
  const ang = (t - 0.22) * Math.PI * 2;
  const el = Math.sin(ang) * 1.05 + 0.06; // small lift keeps dawn/dusk glow visible
  const az = Math.PI * 0.35 + t * Math.PI * 1.1;
  const v = new THREE.Vector3(
    Math.cos(az) * Math.cos(el),
    Math.sin(el),
    Math.sin(az) * Math.cos(el),
  );
  return v.normalize();
}

export interface SkyOpts {
  /** Fog distance multiplier for smaller maps (skirmish: size/5000). Default 1. */
  fogScale?: number;
  /** Sky dome radius. Default 9000 (canonical); skirmish maps pass ~2x the fog far. */
  domeRadius?: number;
  /**
   * When set, the sun casts shadows with an ortho frustum of ±shadowExtent
   * around the map center. Skirmish maps pass halfExtent * 1.2.
   */
  shadowExtent?: number;
}

export function createSky(opts: SkyOpts = {}): SkyRig {
  const fogScale = opts.fogScale ?? 1;
  const group = new THREE.Group();
  const uniforms = {
    uZenith: { value: new THREE.Color() },
    uHorizon: { value: new THREE.Color() },
    uHaze: { value: new THREE.Color() },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uSunColor: { value: new THREE.Color() },
  };
  const dome = new THREE.Mesh(
    new THREE.SphereGeometry(opts.domeRadius ?? 9000, 32, 16),
    new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      uniforms,
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_Position = projectionMatrix * mv;
        }`,
      fragmentShader: /* glsl */ `
        uniform vec3 uZenith; uniform vec3 uHorizon; uniform vec3 uHaze;
        uniform vec3 uSunDir; uniform vec3 uSunColor;
        varying vec3 vDir;
        void main() {
          float h = clamp(vDir.y, -1.0, 1.0);
          vec3 col = mix(uHorizon, uZenith, pow(clamp(h, 0.0, 1.0), 0.55));
          // Warm haze band hugging the horizon.
          float band = exp(-abs(h) * 9.0);
          col = mix(col, uHaze, band * 0.55);
          // Below-horizon: fade to haze (terrain covers it anyway).
          col = mix(col, uHaze * 0.7, smoothstep(0.0, -0.4, h));
          // Sun disc + glow.
          float s = clamp(dot(normalize(vDir), normalize(uSunDir)), 0.0, 1.0);
          col += uSunColor * (pow(s, 900.0) * 1.6 + pow(s, 18.0) * 0.28);
          gl_FragColor = vec4(col, 1.0);
        }`,
    }),
  );
  dome.frustumCulled = false;
  dome.renderOrder = -10;
  group.add(dome);

  const sun = new THREE.DirectionalLight(0xffffff, 2.5);
  if (opts.shadowExtent !== undefined) {
    // RTS sun shadows: static ortho box around the map center.
    sun.castShadow = true;
    const e = opts.shadowExtent;
    sun.shadow.camera.left = -e;
    sun.shadow.camera.right = e;
    sun.shadow.camera.top = e;
    sun.shadow.camera.bottom = -e;
    sun.shadow.camera.near = 1;
    sun.shadow.camera.far = 3000;
    sun.shadow.camera.updateProjectionMatrix();
    sun.shadow.mapSize.set(2048, 2048);
    sun.shadow.bias = -0.0004;
    sun.shadow.normalBias = Math.max(0.4, e * 0.004);
  } else {
    sun.castShadow = false;
  }
  group.add(sun);
  group.add(sun.target);
  const hemi = new THREE.HemisphereLight(0xbdd3e6, 0x3a2f22, 0.75);
  group.add(hemi);

  return {
    group, sun, hemi,
    setTimeOfDay(t: TimeOfDay, scene: THREE.Scene, renderer?: THREE.WebGLRenderer) {
      const k = sampleSky(t);
      uniforms.uZenith.value.copy(k.zenith);
      uniforms.uHorizon.value.copy(k.horizon);
      uniforms.uHaze.value.copy(k.haze);
      uniforms.uSunColor.value.copy(k.sun);
      const sd = sunDirection(t);
      uniforms.uSunDir.value.copy(sd);
      sun.position.copy(sd).multiplyScalar(1000);
      sun.color.copy(k.sun);
      sun.intensity = k.sunI;
      hemi.color.copy(k.hemiSky);
      hemi.groundColor.copy(k.hemiGround);
      hemi.intensity = k.hemiI;
      if (!scene.fog || !(scene.fog as THREE.Fog).isFog) {
        scene.fog = new THREE.Fog(k.fog.clone(), k.fogNear * fogScale, k.fogFar * fogScale);
      } else {
        const fog = scene.fog as THREE.Fog;
        fog.color.copy(k.fog);
        fog.near = k.fogNear * fogScale;
        fog.far = k.fogFar * fogScale;
      }
      if (renderer) renderer.toneMappingExposure = k.exposure;
    },
    dispose() {
      dome.geometry.dispose();
      (dome.material as THREE.Material).dispose();
      group.clear();
    },
  };
}
